import _ from 'lodash';

import { getConcentrationConfigValueType } from '@finley-technologies/calc-agent';
import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  BorrowingBaseReportGQLType,
  ConcentrationLimitCalculationGQLType,
  ConcentrationLimitValueTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

export type VerificationApproveTablesMode = 'approveDeny' | 'viewOnly';

export const isSanitizedJSON = (test: any): test is Util.SanitizedJSON => {
  if (typeof test === 'object') {
    return true;
  }
  return false;
};

type SanitizedJSONWithNumberValues = {
  finleyValue?: number | null;
  borrowerValue?: number | null;
};

export type ExpandedConcentrationLimitCalculations = Pick<
  ConcentrationLimitCalculationGQLType,
  'borrowingBaseReduction' | 'concentration' | 'concentrationAmount'
> & {
  totalEligibleExposure: Util.Maybe<number>;
  concentrationLimitAmount: Util.Maybe<number>;
};

export const isSanitizedJSONWithNumberValues = (
  test: any,
): test is SanitizedJSONWithNumberValues => {
  return (
    isSanitizedJSON(test) &&
    _.has(test, 'finleyValue') &&
    (test.finleyValue === null || _.isNumber(test.finleyValue)) &&
    _.has(test, 'borrowerValue') &&
    (test.borrowerValue === null || _.isNumber(test.borrowerValue))
  );
};

/**
 * In addition to being used for formatting, these functions are used in lieu of an epsilon value
 * to determine floating point equality.
 *
 * The thinking is that the logic that determines whether there is a difference between values
 * should use the same precision as we use when printing values for display.
 */
export const verificationValueTypeToFormatterMap: Record<
  ConcentrationLimitValueTypeGQLType,
  (value: number) => string | undefined
> = {
  currency: (value: number) =>
    commonUtils.numberToCurrencyWithSymbol({ number: value }),
  percentage: (value: number) =>
    commonUtils.numberToFormattedString(value, 'percentage', false),
  number: (value: number) => value.toFixed(2),
};

/**
 * The logic that determines whether there is a difference between values
 * should use the same precision as we use when printing values for display.
 */
export const getBorrowerFinleyDifference = ({
  finleyValue,
  borrowerValue,
  valueType,
}: {
  finleyValue: any;
  borrowerValue: any;
  valueType: ConcentrationLimitValueTypeGQLType;
}): number | undefined => {
  const finleyNumber = typeof finleyValue === 'number' ? finleyValue : 0;
  const borrowerNumber = typeof borrowerValue === 'number' ? borrowerValue : 0;
  const difference = finleyNumber - borrowerNumber;

  const formatFn = verificationValueTypeToFormatterMap[valueType];
  const formattedFinleyValue = formatFn(finleyNumber);
  const formattedBorrowerValue = formatFn(borrowerNumber);
  // Use a string comparison on the formatted value for floating point comparison, in lieu of an
  // epsilon value.
  return difference === 0 || formattedFinleyValue === formattedBorrowerValue
    ? undefined
    : difference;
};

export const formatTotalEligibleExposureLessConcentrationKickoutCalculations = ({
  totalConcentrationKickoutCalculations,
  totalEligibleExposureCalculations,
}: {
  totalConcentrationKickoutCalculations: Util.SanitizedJSON;
  totalEligibleExposureCalculations: Util.SanitizedJSON;
}): Util.SanitizedJSON => {
  const doEligibleExposureAndConcentrationKickoutsExist =
    isSanitizedJSONWithNumberValues(totalEligibleExposureCalculations) &&
    isSanitizedJSONWithNumberValues(totalConcentrationKickoutCalculations);

  const totalEligibleExposureLessConcentrationKickoutsFinleyValue =
    doEligibleExposureAndConcentrationKickoutsExist &&
    _.isNumber(totalEligibleExposureCalculations.finleyValue) &&
    _.isNumber(totalConcentrationKickoutCalculations.finleyValue)
      ? totalEligibleExposureCalculations.finleyValue -
        totalConcentrationKickoutCalculations.finleyValue
      : undefined;
  const totalEligibleExposureLessConcentrationKickoutsBorrowerValue =
    doEligibleExposureAndConcentrationKickoutsExist &&
    _.isNumber(totalEligibleExposureCalculations.borrowerValue) &&
    _.isNumber(totalConcentrationKickoutCalculations.borrowerValue)
      ? totalEligibleExposureCalculations.borrowerValue -
        totalConcentrationKickoutCalculations.borrowerValue
      : undefined;

  return {
    finleyValue: totalEligibleExposureLessConcentrationKickoutsFinleyValue,
    borrowerValue: totalEligibleExposureLessConcentrationKickoutsBorrowerValue,
  };
};

export const formatExpandedVerificationReportConcentrationLimitCalculations = ({
  concentrationLimitCalculations,
  totalEligibleExposureReport,
}: {
  concentrationLimitCalculations: Util.SanitizedJSON;
  totalEligibleExposureReport: Util.SanitizedJSON;
}): Util.SanitizedJSON => {
  const { concentrationLimit } = concentrationLimitCalculations;

  // get concentration limit amount
  const doEligibleExposureAndConcentrationLimitExist =
    isSanitizedJSONWithNumberValues(totalEligibleExposureReport) &&
    isSanitizedJSONWithNumberValues(concentrationLimit);

  const concentrationLimitAmountFinleyValue =
    doEligibleExposureAndConcentrationLimitExist &&
    _.isNumber(totalEligibleExposureReport.finleyValue) &&
    _.isNumber(concentrationLimit.finleyValue)
      ? totalEligibleExposureReport.finleyValue * concentrationLimit.finleyValue
      : undefined;
  const concentrationLimitAmountBorrowerValue =
    doEligibleExposureAndConcentrationLimitExist &&
    _.isNumber(totalEligibleExposureReport.borrowerValue) &&
    _.isNumber(concentrationLimit.borrowerValue)
      ? totalEligibleExposureReport.borrowerValue *
        concentrationLimit.borrowerValue
      : undefined;

  const expandedConcentrationLimitCalculations = {
    ...concentrationLimitCalculations,
    totalEligibleExposure: {
      finleyValue: totalEligibleExposureReport.finleyValue,
      borrowerValue: totalEligibleExposureReport.borrowerValue,
    },
    concentrationLimitAmount: {
      finleyValue: concentrationLimitAmountFinleyValue,
      borrowerValue: concentrationLimitAmountBorrowerValue,
    },
  };

  return expandedConcentrationLimitCalculations;
};

export const getConcentrationLimitBorrowingBaseReductionFromBorrowingBaseReport = ({
  previousBorrowingBaseReport,
  concentrationLimitKey,
}: {
  previousBorrowingBaseReport?: BorrowingBaseReportGQLType;
  concentrationLimitKey: string;
}): number | undefined => {
  if (!previousBorrowingBaseReport) {
    return undefined;
  }
  const prevConcentrationLimitCalculations = _.find(
    previousBorrowingBaseReport.concentrationLimitCalculations,
    (concentrationLimitCalc) =>
      concentrationLimitCalc.concentrationLimitKey === concentrationLimitKey,
  );
  if (!prevConcentrationLimitCalculations) {
    return undefined;
  }

  return prevConcentrationLimitCalculations.borrowingBaseReduction;
};

export const formatConcentrationLimitAmountTooltipLabel = ({
  concentrationLimitConfig,
  totalEligibleExposureReport,
}: {
  concentrationLimitConfig: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  totalEligibleExposureReport: Util.SanitizedJSON;
}): string | undefined => {
  const valueConfig =
    concentrationLimitConfig._config_?.concentrationLimitConfig?.valueConfig;
  const valueType = getConcentrationConfigValueType(
    concentrationLimitConfig?._config_.concentrationLimitConfig,
  );
  const formatterFn = valueConfig
    ? verificationValueTypeToFormatterMap[valueType]
    : null;

  const formattedConcentrationLimit =
    // @limit_formula_todo: formulas requires evaluating config.
    //  revisit this implementation.
    formatterFn && valueConfig && typeof valueConfig.limit === 'number'
      ? formatterFn(valueConfig.limit)
      : valueConfig?.limit;
  const doEligibleExposureAndConcentrationLimitExist =
    isSanitizedJSONWithNumberValues(totalEligibleExposureReport) &&
    formattedConcentrationLimit;
  const concentrationLimitAmountTooltipLabel =
    doEligibleExposureAndConcentrationLimitExist &&
    _.isNumber(totalEligibleExposureReport.finleyValue)
      ? `Total eligible exposure of ${verificationValueTypeToFormatterMap.currency(
          totalEligibleExposureReport.finleyValue,
        )} multiplied by concentation limit of ${formattedConcentrationLimit}`
      : undefined;
  return concentrationLimitAmountTooltipLabel;
};

export const formatConcentrationTooltipLabel = ({
  concentrationAmountReport,
  totalEligibleExposureReport,
}: {
  concentrationAmountReport: Util.SanitizedJSON;
  totalEligibleExposureReport: Util.SanitizedJSON;
}): string | undefined => {
  const doEligibleExposureAndConcentrationAmountExist =
    isSanitizedJSONWithNumberValues(concentrationAmountReport) &&
    isSanitizedJSONWithNumberValues(totalEligibleExposureReport);

  const concentrationTooltipLabel =
    doEligibleExposureAndConcentrationAmountExist &&
    _.isNumber(totalEligibleExposureReport.finleyValue) &&
    _.isNumber(concentrationAmountReport.finleyValue)
      ? `Concentration amount of ${verificationValueTypeToFormatterMap.currency(
          concentrationAmountReport.finleyValue,
        )} divided by total eligible exposure of ${verificationValueTypeToFormatterMap.currency(
          totalEligibleExposureReport.finleyValue,
        )}`
      : undefined;

  return concentrationTooltipLabel;
};

export const formatConcentrationKickoutTooltipLabel = ({
  concentrationAmountReport,
  concentrationLimitAmountReport,
}: {
  concentrationAmountReport: Util.SanitizedJSON;
  concentrationLimitAmountReport: Util.SanitizedJSON;
}): string | undefined => {
  const doEligibleExposureAndConcentrationAmountExist =
    isSanitizedJSONWithNumberValues(concentrationAmountReport) &&
    isSanitizedJSONWithNumberValues(concentrationLimitAmountReport);

  const concentrationTooltipLabel =
    doEligibleExposureAndConcentrationAmountExist &&
    _.isNumber(concentrationLimitAmountReport.finleyValue) &&
    _.isNumber(concentrationAmountReport.finleyValue)
      ? `Concentration amount of ${verificationValueTypeToFormatterMap.currency(
          concentrationAmountReport.finleyValue,
        )} minus concentration limit amount of ${verificationValueTypeToFormatterMap.currency(
          concentrationLimitAmountReport.finleyValue,
        )}`
      : undefined;

  return concentrationTooltipLabel;
};

export const isDeclarativeFilterConcentrationLimitCalculationConfig = (
  calculationConfig: any,
): calculationConfig is CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterCalculationMethodConfig => {
  return CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeConfigIterable.includes(
    calculationConfig?.type,
  );
};
export const isWeightedAverageConcentrationLimitCalculationConfig = (
  calculationConfig: any,
): calculationConfig is CreditAgreement.Config.ConcentrationLimit.Calculation.DeclarativeFilterWeightedAverageCalculationConfig => {
  return calculationConfig?.type === 'weightedAverage';
};
